"use client";
// @ts-strict-ignore
import React, { useCallback, useEffect, useState } from "react";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { captureException } from "observability";
import { mediaQuery, useToaster } from "@hubble/web";
import { ConverterFormWrapper, InputGroup } from "../styles";
import SelectCurrencyModal, {
  SUPPORTED_CURRENCY_SELECTION_MODES
} from "../../SelectCurrencyModal";
import { useConverterPageContext } from "utils/converter-provider";
import { useRouter } from "i18n/routing";
import { useMedia } from "react-use";
import queryStringPkg from "query-string";
import { pairPriceFetcherFunction } from "../utils";
import { mixpanelClient } from "analytics/mixpanel";
import { EVENTS } from "analytics/mixpanel/events";
import { isCryptoSymbol } from "../../utils";
import {
  DEFAULT_BASE_CURRENCY,
  FIAT_CURRENCIES,
  NUMBER_REGEX,
  TEST_IDS
} from "constants/converter";
import SwapButton from "./SwapButton";
import type { CurrencyType } from "types/converter";
import CurrencyInput from "./CurrencyInput";

interface ConverterFormProps {
  exchangeRate: number;
}

const ConverterForm: React.FC<ConverterFormProps> = ({ exchangeRate }) => {
  const router = useRouter();
  const isMobileXsDown = useMedia(mediaQuery.mobileXsDown, false);
  const { showToast } = useToaster();
  const { featureVariables } = useFeatureFlags();
  const [isCurrencyUpdated, setIsCurrencyUpdated] = useState(false);

  const { baseSlug, quoteSlug } = useConverterPageContext();

  const [isSwapEnabled, setIsSwapEnabled] = useState(false);
  const [currencyModalState, setCurrencyModalState] = useState({
    open: false,
    mode: SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
  });

  const convertValues = useCallback(
    (isSwapMode = false, value = "") => {
      // Convert based on current mode
      if (!value) return "";
      const convertedValue = String(
        !isSwapMode
          ? parseFloat(value) * exchangeRate // Crypto to Fiat
          : parseFloat(value) / exchangeRate // Fiat to Crypto
      );

      return convertedValue;
    },
    [exchangeRate]
  );
  const [baseCurrency, setBaseCurrency] = useState<CurrencyType>({
    symbol: baseSlug?.toUpperCase(),
    value: "1"
  });
  const [quoteCurrency, setQuoteCurrency] = useState<CurrencyType>({
    symbol: quoteSlug?.toUpperCase(),
    value: convertValues(isSwapEnabled, baseCurrency.value)
  });
  const handlePrimaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if the input is numeric and positive
    if (!NUMBER_REGEX.test(value)) return; // This regex allows only digits and a single decimal point

    setBaseCurrency({ ...baseCurrency, value });
    setQuoteCurrency({
      ...quoteCurrency,
      value: convertValues(isSwapEnabled, value)
    });
  };

  const handleSecondaryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if the input is numeric and positive
    if (!NUMBER_REGEX.test(value)) return; // Same regex as above

    setQuoteCurrency({ ...quoteCurrency, value });
    const convertedValue = convertValues(!isSwapEnabled, value);
    setBaseCurrency({ ...baseCurrency, value: convertedValue });
  };

  const handleSwap = useCallback(() => {
    // Swap the currency types and values, ensuring conversion is consistent with the new setup

    setBaseCurrency({ ...quoteCurrency, value: baseCurrency.value });

    setQuoteCurrency({
      ...baseCurrency,
      value: convertValues(!isSwapEnabled, baseCurrency.value)
    });

    // Toggle the swap mode
    setIsSwapEnabled((prevSwap) => !prevSwap);
  }, [baseCurrency, convertValues, isSwapEnabled, quoteCurrency]);

  const showUnsupportedToast = () => {
    showToast({ message: "This currency pair is not supported currently." });
  };

  const handleCurrencySelection = (symbol: string) => {
    let newBaseSlug, newQuoteSlug;
    if (
      currencyModalState.mode ===
      SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
    ) {
      newQuoteSlug = isSwapEnabled ? baseCurrency.symbol : quoteCurrency.symbol;
      newBaseSlug = symbol;
    } else {
      newQuoteSlug = symbol;
      newBaseSlug = !isSwapEnabled ? baseCurrency.symbol : quoteCurrency.symbol;
    }

    // Ensure the base slug is a crypto symbol
    if (!isCryptoSymbol(newBaseSlug)) {
      if (!isCryptoSymbol(newQuoteSlug)) {
        // swap newBaseSlug and newQuoteSlug
        const temp = newBaseSlug;
        newBaseSlug = newQuoteSlug;
        newQuoteSlug = temp;
      } else {
        // keeping BTC as default crypto symbol
        newBaseSlug = DEFAULT_BASE_CURRENCY;
      }
    }

    // track pair selection event
    const { search } = window.location;
    mixpanelClient.track(EVENTS.CONVERTER_FORM_PAIR_SELECTION.name, {
      [EVENTS.CONVERTER_FORM_PAIR_SELECTION.properties.BASE_CURRENCY]:
        newBaseSlug,
      [EVENTS.CONVERTER_FORM_PAIR_SELECTION.properties.QUOTE_CURRENCY]:
        newQuoteSlug,
      ...queryStringPkg.parse(search)
    });

    // Close the currency selection modal immediately
    setCurrencyModalState((prevState) => ({ ...prevState, open: false }));

    // Fetch the new pair price
    // to validate whether the being-selected pair is a valid pair
    // setIsRouting(true);
    // Function to update the exchange rate and handle currency changes based on user selection
    pairPriceFetcherFunction("", newBaseSlug, newQuoteSlug, featureVariables)
      .then((exchangeRate) => {
        // Check if the fetched exchange rate is valid and not zero
        if (exchangeRate !== "0") {
          setBaseCurrency({ ...baseCurrency, symbol: newBaseSlug });
          setQuoteCurrency({ ...quoteCurrency, symbol: newQuoteSlug });
          setIsCurrencyUpdated(true);
        } else {
          // Show toast notification if the pair is unsupported
          showUnsupportedToast();
          // Close the currency selection modal
          setCurrencyModalState((prevState) => ({ ...prevState, open: true }));
        }
      })
      .catch((error) => {
        captureException(error as Error);
        // Show toast notification if fetching the exchange rate fails
        showUnsupportedToast();
        mixpanelClient.track(EVENTS.CONVERTER_FORM_UNSUPPORTED_PAIR.name, {
          [EVENTS.CONVERTER_FORM_PAIR_SELECTION.properties.BASE_CURRENCY]:
            newBaseSlug,
          [EVENTS.CONVERTER_FORM_PAIR_SELECTION.properties.QUOTE_CURRENCY]:
            newQuoteSlug,
          ...queryStringPkg.parse(search)
        });
      })
      .finally(() => {
        // Actions to always execute after the promise settles
        // setIsRouting(false); // Stop the routing indicator

        // Close the currency modal on mobile if screen size is extra small
        if (isMobileXsDown) {
          setCurrencyModalState((prevState) => ({ ...prevState, open: false }));
        }
      });
  };

  const handleExchangeRateChange = () => {
    setQuoteCurrency({
      ...quoteCurrency,
      value: convertValues(isSwapEnabled, baseCurrency.value)
    });
  };

  useEffect(() => {
    handleExchangeRateChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exchangeRate]);

  useEffect(() => {
    if (isCurrencyUpdated) {
      router.push(`/converter/${baseCurrency.symbol}/${quoteCurrency.symbol}`);

      // reset swap
      setIsSwapEnabled(false);

      // Reset the isCurrencyUpdated state
      setIsCurrencyUpdated(false);
    }
  }, [
    baseCurrency.symbol,
    handleSwap,
    isCurrencyUpdated,
    isSwapEnabled,
    quoteCurrency.symbol,
    router
  ]);

  const computeHiddenSymbols = () => {
    return [
      ...(currencyModalState.mode ===
      SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
        ? [quoteCurrency.symbol, baseCurrency.symbol, ...FIAT_CURRENCIES]
        : [baseCurrency.symbol, quoteCurrency.symbol])
    ];
  };

  return (
    <ConverterFormWrapper>
      <SelectCurrencyModal
        isOpen={currencyModalState.open}
        handleCurrencySelection={(symbol) => handleCurrencySelection(symbol)}
        selectionMode={currencyModalState.mode}
        onClose={() =>
          setCurrencyModalState((prevState) => ({ ...prevState, open: false }))
        }
        hiddenSymbols={computeHiddenSymbols()}
      />
      <InputGroup>
        <CurrencyInput
          testId={TEST_IDS.PRIMARY_INPUT_FIELD}
          name="primary-input-field"
          value={baseCurrency.value}
          onChange={handlePrimaryChange}
          shouldShowLoader={
            !isSwapEnabled
              ? currencyModalState.mode ===
                SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
              : currencyModalState.mode ===
                SUPPORTED_CURRENCY_SELECTION_MODES.QUOTE_CURRENCY
          }
          currency={baseCurrency}
          onCurrencySelectionClick={() =>
            setCurrencyModalState({
              open: true,
              mode: !isSwapEnabled
                ? SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
                : SUPPORTED_CURRENCY_SELECTION_MODES.QUOTE_CURRENCY
            })
          }
          exchangeRate={exchangeRate}
        />
        <SwapButton handleSwap={handleSwap} />
        <CurrencyInput
          testId={TEST_IDS.SECONDARY_INPUT_FIELD}
          name="secondary-input-field"
          value={quoteCurrency.value}
          onChange={handleSecondaryChange}
          currency={quoteCurrency}
          onCurrencySelectionClick={() =>
            setCurrencyModalState({
              open: true,
              mode: isSwapEnabled
                ? SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
                : SUPPORTED_CURRENCY_SELECTION_MODES.QUOTE_CURRENCY
            })
          }
          exchangeRate={exchangeRate}
          shouldShowLoader={
            !isSwapEnabled
              ? currencyModalState.mode ===
                SUPPORTED_CURRENCY_SELECTION_MODES.QUOTE_CURRENCY
              : currencyModalState.mode ===
                SUPPORTED_CURRENCY_SELECTION_MODES.BASE_CURRENCY
          }
        />
      </InputGroup>
    </ConverterFormWrapper>
  );
};

export default ConverterForm;
